/**
 * PageTransitionStage — animates between `oldLayer` (the slide being
 * left) and `newLayer` (the slide being entered).
 *
 * Two renderers:
 *   - `FramerStage` for `transition.kind === 'fm'` (folder-1
 *     transitions that ship framer-motion Variants).
 *   - `CssStage` for `transition.kind === 'css'` (folder-2
 *     transitions driven by `[data-t="..."]` keyframes in
 *     `page-transitions.css`).
 *
 * Falls back to a plain opacity cross-fade when `transition` is null
 * (the deck-level "built-in iris-cut default" — see
 * [data-t="iris"] in page-transitions.css).
 *
 * Z-index inside the transition subtree is capped at 5:
 *   .pt-stage          → 3
 *   .pt-layer-new      → 4
 *   .pt-layer-old      → 5
 *   .pt-glitch-lines   → 5 (peeks above old layer during glitch)
 *   .pt-liquid-veil    → 5 (peeks above during liquid)
 * Bumping the cap keeps the stage safe to nest inside other layers
 * (intro overlay, respond dialog, controls) without leaking a black
 * curtain onto them.
 */
import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import {
  reviveFramerVariants,
  type ProposalTransition,
} from '@/lib/transitions/catalogue';
import { FXFilters } from './FXFilters';
import './page-transitions.css';

interface Props {
  /** Stable identifier for the currently-rendered slide (e.g. `${index}`). */
  slideKey: string;
  /** Direction of the last advance: 1 forward, -1 back. Defaults to 1. */
  direction?: 1 | -1;
  /**
   * Page transition to render. `null`/missing = the built-in iris-cut
   * default (`[data-t="iris"]` in page-transitions.css).
   */
  transition: ProposalTransition | null;
  /** What to render for the OLD layer (the slide being left). When
   *  `null`, only the NEW layer renders (initial mount). */
  oldLayer: ReactNode;
  /** What to render for the NEW layer (the slide being entered). */
  newLayer: ReactNode;
}

export function PageTransitionStage({
  slideKey,
  direction = 1,
  transition,
  oldLayer,
  newLayer,
}: Props) {
  if (!transition) {
    // No iris fallback — render the new layer directly so the stage
    // never sits with an opaque-black backdrop over the slide.
    return (
      <div className="pt-stage pt-stage--no-transition" key={`stage-${slideKey}`}>
        {newLayer}
      </div>
    );
  }
  if (transition.kind === 'fm') {
    return (
      <FramerStage
        slideKey={slideKey}
        direction={direction}
        transition={transition}
        oldLayer={oldLayer}
        newLayer={newLayer}
      />
    );
  }
  return (
    <>
      <FXFilters />
      <CssStage
        slideKey={slideKey}
        direction={direction}
        transition={transition}
        oldLayer={oldLayer}
        newLayer={newLayer}
      />
    </>
  );
}

/* ─── framer-motion renderer ──────────────────────────────────────────── */

function FramerStage({
  slideKey,
  direction,
  transition,
  oldLayer,
  newLayer,
}: {
  slideKey: string;
  direction: 1 | -1;
  transition: ProposalTransition;
  // `oldLayer` is intentionally unused — framer-motion's AnimatePresence
  // unmounts the previous child during exit, so we only render the NEW
  // layer here.
  oldLayer?: ReactNode;
  newLayer: ReactNode;
}) {
  const variants: Variants = useMemo(() => {
    if (transition.kind !== 'fm' || !transition.framerVariants) {
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
        exit: { opacity: 0, transition: { duration: 0.4, ease: 'easeIn' } },
      };
    }
    return reviveFramerVariants(transition.framerVariants);
  }, [transition]);

  // Folder-1 transitions `cube` / `coverflow` / `flip` rotate the
  // layer around Y. CSS `perspective` is required on the *parent* for
  // the rotate to actually look 3D — otherwise the rotation renders
  // flat.
  const perspective =
    transition.kind === 'fm'
      ? transition.id === 'coverflow'
        ? '1200px'
        : transition.id === 'flip'
          ? '1600px'
          : transition.id === 'cube'
            ? '1400px'
            : undefined
      : undefined;

  return (
    <div
      className="pt-stage"
      style={{
        perspective,
        perspectiveOrigin: '50% 50%',
      }}
    >
      <AnimatePresence custom={direction} mode="popLayout" initial={false}>
        <motion.div
          key={slideKey}
          custom={direction}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            willChange: 'transform, opacity, filter',
          }}
        >
          {newLayer}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ─── CSS-keyframe renderer ──────────────────────────────────────────── */

const easeInCubic = (p: number) => p * p * p;
const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3);

function CssStage({
  slideKey,
  direction,
  transition,
  oldLayer,
  newLayer,
}: {
  slideKey: string;
  direction: 1 | -1;
  transition: ProposalTransition | null;
  oldLayer: ReactNode;
  newLayer: ReactNode;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const id = transition?.cssDataT ?? '';
  const dur = transition?.cssDurationMs ?? 700;

  useEffect(() => {
    if (id !== 'dissolve' && id !== 'ripple') return;
    const dispA = document.getElementById('fxDissolveA');
    const dispB = document.getElementById('fxDissolveB');
    const dispR = document.getElementById('fxRipple');
    for (const tid of ['fxTurbA', 'fxTurbB', 'fxTurbR']) {
      const t = document.getElementById(tid);
      t?.setAttribute('seed', String(Math.floor(Math.random() * 90) + 2));
    }
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      if (id === 'dissolve') {
        dispA?.setAttribute('scale', String(30 + easeInCubic(p) * 1150));
        dispB?.setAttribute('scale', String((1 - easeOutCubic(p)) * 950));
      } else {
        dispR?.setAttribute('scale', String(Math.sin(p * Math.PI) * 520));
      }
      if (p < 1) raf = requestAnimationFrame(tick);
      else {
        dispA?.setAttribute('scale', '0');
        dispB?.setAttribute('scale', '0');
        dispR?.setAttribute('scale', '0');
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [id, slideKey, dur]);

  const stageStyle: CSSProperties = {
    '--dx': direction,
    '--dy': direction,
  } as CSSProperties;

  if (id === 'glitch') {
    const slices = 7;
    return (
      <div
        ref={stageRef}
        className="pt-stage"
        data-t={id}
        style={stageStyle}
        key={`stage-${slideKey}`}
      >
        <div
          className="pt-stage-inner"
          style={{ position: 'absolute', inset: 0 }}
        >
          {Array.from({ length: slices }).map((_, i) => {
            const sh = 100 / slices;
            return (
              <div
                key={`slice-${i}-${slideKey}`}
                className="pt-layer pt-g-slice"
                style={{
                  clipPath: `inset(${i * sh}% 0 ${100 - (i + 1) * sh}% 0)`,
                  animationDelay: `${i * 42}ms`,
                }}
              >
                {oldLayer}
              </div>
            );
          })}
        </div>
        <div className="pt-layer pt-layer-new" key={`new-${slideKey}`}>
          {newLayer}
        </div>
        <div className="pt-glitch-lines" />
      </div>
    );
  }

  const isLiquid = id === 'liquid';

  return (
    <div
      ref={stageRef}
      className="pt-stage"
      data-t={id}
      style={stageStyle}
      key={`stage-${slideKey}`}
    >
      <div className="pt-layer pt-layer-old" key={`old-${slideKey}`}>
        {oldLayer}
      </div>
      <div className="pt-layer pt-layer-new" key={`new-${slideKey}`}>
        {newLayer}
      </div>
      {isLiquid && <div className="pt-liquid-veil" key={`veil-${slideKey}`} />}
    </div>
  );
}
